import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, FlaskConical, LayoutGrid, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import type { IdeaTask, Experiment } from '@/lib/types';
import { cn } from '@/lib/utils';

interface NextActionsPanelProps {
  ideaId: string;
  tasks: IdeaTask[];
  risks: string[];
  onTasksCreated?: () => void;
  onExperimentsCreated?: () => void;
}

export function NextActionsPanel({
  ideaId,
  tasks,
  risks,
  onTasksCreated,
  onExperimentsCreated,
}: NextActionsPanelProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [creatingExperiments, setCreatingExperiments] = useState(false);
  const [tasksCreated, setTasksCreated] = useState(false);
  const [experimentsCreated, setExperimentsCreated] = useState(false);

  // Check if tasks already exist for this idea in "next" status
  const hasNextTasks = tasks.filter(t => t.status === 'next').length > 0;
  
  // Create tasks from plan (tasks are already in idea_tasks, this just confirms and navigates)
  const handleCreateTasks = async () => {
    if (!user || hasNextTasks) {
      // Tasks already exist, just navigate
      toast.success('Tasks ready!');
      navigate(`/app/plan/${ideaId}`);
      return;
    }
    
    setCreatingTasks(true);
    
    try {
      // Tasks should already be created during plan generation
      // This action confirms and navigates to board
      setTasksCreated(true);
      toast.success('Tasks created successfully!');
      onTasksCreated?.();
      
      // Auto-redirect to Build Board after short delay
      setTimeout(() => {
        navigate(`/app/plan/${ideaId}`);
      }, 500);
    } catch (error) {
      console.error('Error creating tasks:', error);
      toast.error('Failed to create tasks');
    } finally {
      setCreatingTasks(false);
    }
  };

  // Create experiments from risks/assumptions
  const handleCreateExperiments = async () => {
    if (!user || !ideaId) return;
    
    if (risks.length === 0) {
      toast.info('No risks or assumptions to convert to experiments');
      return;
    }
    
    setCreatingExperiments(true);
    
    try {
      // Convert risks to experiments
      const experimentsToInsert = risks.map((risk) => ({
        user_id: user.id,
        idea_id: ideaId,
        hypothesis: risk,
        method: null,
        metric: null,
        result: null,
        status: 'pending',
      }));
      
      // Use any cast until Supabase types are regenerated
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('experiments').insert(experimentsToInsert);
      
      if (error) {
        throw error;
      }
      
      setExperimentsCreated(true);
      toast.success(`${risks.length} experiments created!`);
      onExperimentsCreated?.();
    } catch (error) {
      console.error('Error creating experiments:', error);
      toast.error('Failed to create experiments');
    } finally {
      setCreatingExperiments(false);
    }
  };

  // Navigate to Build Board (Kanban)
  const handleOpenBuildBoard = () => {
    navigate(`/app/plan/${ideaId}`);
  };

  return (
    <div className="sticky bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur-sm px-4 py-3 z-20">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 max-w-4xl mx-auto">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide shrink-0 hidden sm:block">
          Next Actions
        </span>
        
        <div className="flex flex-col sm:flex-row gap-2 flex-1 sm:justify-end">
          {/* Create Tasks */}
          <Button
            variant={tasksCreated || hasNextTasks ? "secondary" : "default"}
            size="sm"
            onClick={handleCreateTasks}
            disabled={creatingTasks}
            className={cn(
              "gap-1.5 flex-1 sm:flex-none",
              (tasksCreated || hasNextTasks) && "bg-green-500/10 text-green-600 hover:bg-green-500/20"
            )}
          >
            {creatingTasks ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (tasksCreated || hasNextTasks) ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <ListChecks className="h-3.5 w-3.5" />
            )}
            <span className="hidden xs:inline">
              {(tasksCreated || hasNextTasks) ? 'Tasks Ready' : 'Create Tasks'}
            </span>
            <span className="xs:hidden">Tasks</span>
          </Button>
          
          {/* Create Experiments */}
          <Button
            variant={experimentsCreated ? "secondary" : "outline"}
            size="sm"
            onClick={handleCreateExperiments}
            disabled={creatingExperiments || risks.length === 0}
            className={cn(
              "gap-1.5 flex-1 sm:flex-none",
              experimentsCreated && "bg-green-500/10 text-green-600 hover:bg-green-500/20"
            )}
          >
            {creatingExperiments ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : experimentsCreated ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <FlaskConical className="h-3.5 w-3.5" />
            )}
            <span className="hidden xs:inline">
              {experimentsCreated ? 'Experiments Ready' : 'Create Experiments'}
            </span>
            <span className="xs:hidden">Experiments</span>
            {!experimentsCreated && risks.length > 0 && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-muted ml-0.5">
                {risks.length}
              </span>
            )}
          </Button>
          
          {/* Open Build Board */}
          <Button
            variant="default"
            size="sm"
            onClick={handleOpenBuildBoard}
            className="gap-1.5 flex-1 sm:flex-none"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            <span className="hidden xs:inline">Open Build Board</span>
            <span className="xs:hidden">Board</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
